import {Vec2} from "./vec2";

export class Rect {
  min: Vec2;
  max: Vec2;

  private constructor(min: Vec2, max: Vec2) {
    this.min = min;
    this.max = max;
  }

  /** An empty Rect, represented by maximum and minimum corner points at negative and positive infinity, respectively. */
  static empty(): Rect {
    return new Rect(
      new Vec2(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
      new Vec2(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY),
    );
  }

  static fromMinMax(min: Vec2, max: Vec2): Rect {
    return new Rect(min, max);
  }

  static fromMinSize(min: Vec2, size: Vec2): Rect {
    return new Rect(min, min.add(size));
  }

  static fromCenterSize(center: Vec2, size: Vec2): Rect {
    const halfSize = size.divScalar(2);
    return new Rect(center.sub(halfSize), center.add(halfSize));
  }

  get size(): Vec2 {
    return this.max.sub(this.min);
  }

  get center(): Vec2 {
    return this.min.add(this.size.divScalar(2));
  }

  isEmpty(): boolean {
    return this.min.x === Number.POSITIVE_INFINITY;
  }

  /**
   * Build a new rectangle formed of the union of this rectangle and a point.
   * The union is the smallest rectangle enclosing both the rectangle and the point.
   */
  unionPoint(point: Vec2): Rect {
    if (this.isEmpty()) {
      return new Rect(point.clone(), point.clone());
    }

    return new Rect(
      new Vec2(Math.min(this.min.x, point.x), Math.min(this.min.y, point.y)),
      new Vec2(Math.max(this.max.x, point.x), Math.max(this.max.y, point.y)),
    );
  }

  expand(padding: number): Rect {
    return new Rect(this.min.addScalar(-padding), this.max.addScalar(padding));
  }

  scale(factor: number): Rect {
    return new Rect(this.min.mulScalar(factor), this.max.mulScalar(factor));
  }
}
